// ft3d-hud/row-anatomy.ts — read the PARTS of a rendered log row.
//
// The log renderer builds every row to one shape (log-render.ts `_wrapRow`): a thumbnail cell, a
// body, an optional big-result column, and a timestamp footer. The HUD log wants those parts
// separately — the actor as a portrait, the body as prose, the time as a bare clock reading — so
// this file names the shape in one place instead of scattering selectors through the projector.
//
// It reads MARKUP, deliberately, for the same reason the rest of this bundle does: the row is
// already private-roll redacted, so nothing here has to re-decide what a viewer may see.

import type { FtLogActor } from './types';

/** `_wrapRow` puts the roller's portrait in a bordered col-3 at the head of the row. */
const THUMBNAIL_CELL = '.col-3.border-end';
/**
 * The TARGET's portrait block, as the renderer explicitly marks it.
 *
 * ⚠ The marker is what makes a target visible on the compact line. A renderer that centers a
 * `roll_to` portrait in its own markup instead of calling `_roleToBlock` MUST carry it — the
 * award/level-up rows read as "Azbolg Awarded 1 Experience Marks" with no recipient for exactly
 * that reason (owner 2026-09-14): the GM rolled it, but the player it happened to was dropped.
 */
const TARGET_MARKER = '[data-ft-log-target]';
/**
 * The shape `_roleToBlock` emitted before the marker existed. Kept as a FALLBACK only: the history
 * seeder re-reads rows that were rendered by an older bundle, and those carry no marker.
 */
const TARGET_BLOCK_LEGACY = '.col-12.pt-2';
/** `renderLogTimestamp` stamps both readings at this exact inline size, and nothing else does. */
const TIMESTAMP_SPAN = 'span[style*="font-size: 10px"], span[style*="font-size:10px"]';

/**
 * Phrases the log spells out that a glyph says faster. Matched against the row's leading prose, so
 * an unmatched row simply keeps its words — this list is an improvement, never a requirement.
 * Ordered: the first match wins, so put the longer phrase first where two overlap.
 */
const PHRASE_ICONS: Array<[RegExp, string]> = [
    [/\bgm pushed (campaign )?time\b/i, 'gm_pushed_campaign_time'],
    [/\bgm pulled (campaign )?time\b/i, 'gm_pulled_campaign_time'],
    [/\bcampaign downtime\s*[—-]?\s*time pushed\b/i, 'campaign_downtime_time_pushed'],
    [/\bstatus effect applied\b/i, 'status_effect'],
    [/\bstatus effect removed\b/i, 'status_effect'],
    [/\bis concentrating\b/i, 'concentration'],
    [/\bend concentration\b/i, 'concentration'],
    [/\breverted shapeshift\b/i, 'shapeshift'],
    [/\bcombat (started|ended)\b/i, 'combat'],
    [/\bstart(s|ed)? (their )?turn\b/i, 'start_turn'],
    [/\bapplied\b/i, 'status_effect'],
];

/**
 * The full log renderer already made the context-aware icon choice (roll type, item UUID, mapped
 * destination alias, dynamic charge direction, and so on). `_icon()` preserves that key on its
 * otherwise text-less wrapper so the compact log can reuse it instead of maintaining a second,
 * inevitably incomplete roll-type map. Only the first survives: one leading glyph is useful; all
 * secondary/icon-only detail belongs to the expandable full row and would bloat the ticker.
 */
export function renderedEventIconFor(row: Element): string | undefined {
    // Skill/attack/contest renderers contain both a generic roll-type icon and the actual skill.
    // Prefer the renderer-marked subject icon, then fall back to the first ordinary event icon.
    const preferred = row.querySelector([
        '[data-ft-log-preferred-icon] [data-ft-log-icon]',
        '[data-ft-log-preferred-icon][data-ft-log-icon]',
    ].join(', '));
    // A selector list still returns the first node in document order, not the first selector's
    // match. Query the fallback separately or the earlier generic roll icon wins every time.
    const icon = preferred || row.querySelector('[data-ft-log-icon]');
    const key = icon?.getAttribute('data-ft-log-icon')?.trim();
    return key || undefined;
}

export function timestampSpans(row: Element): Element[] {
    return Array.from(row.querySelectorAll(TIMESTAMP_SPAN));
}

/**
 * Both readings the row footer carries, verbatim: the CAMPAIGN clock ("431-10-13 | 12:58:48 AM")
 * and the real-world one.
 *
 * ⛔ Neither belongs on the collapsed line — the owner does not want campaign time on screen by
 * default. They are held for the expanded detail footer, which is the point of capturing them now:
 * everything a row can show is taken from markup the page already has, so expanding costs no round
 * trip and no second copy of the campaign data.
 */
export function timesFrom(row: Element): { campaign?: string; real?: string } {
    const readings = timestampSpans(row)
        .map((span) => (span.textContent || '').trim())
        .filter((text) => text !== '');
    return { campaign: readings[0], real: readings[1] };
}

/** Read a portrait cell — the shape is identical for the roller and for the target. */
export function actorFromCell(cell: Element | null): FtLogActor | undefined {
    if (!cell) return undefined;
    // The clickable attributes sit on the wrapper for the target block and on a descendant for the
    // roller's cell, so accept either — reading only descendants dropped the target's item id and
    // left its name unclickable.
    const link = cell.matches('[data-ft-item-id]') ? cell : cell.querySelector('[data-ft-item-id]');
    const itemId = link?.getAttribute('data-ft-item-id') || '';
    const img = cell.querySelector('img');
    // The cell's tooltip is the creature name; the img alt carries it too.
    const name = (
        link?.getAttribute('data-bs-original-title')
        || link?.getAttribute('title')
        || img?.getAttribute('alt')
        || ''
    ).trim();
    const picUrl = img?.getAttribute('src') || undefined;
    // No picture → the renderer drew a two-letter box; reuse those letters rather than inventing any.
    const initials = picUrl ? undefined : (cell.textContent || '').trim().slice(0, 2) || undefined;
    if (!itemId && !name && !picUrl && !initials) return undefined;
    return { name, itemId, picUrl, initials };
}

/** The roller: portrait (or initials) plus the clickable id the renderer already put on the cell. */
export function actorFrom(row: Element): FtLogActor | undefined {
    return actorFromCell(row.querySelector(THUMBNAIL_CELL));
}

/**
 * The one place that decides which block IS the target — used both to read the target and to strip
 * it out of the prose, so the two can never disagree about which node that is.
 *
 * ⛔ Never collapse these into one `querySelector('[data-ft-log-target], .col-12.pt-2')`. A selector
 * LIST returns the first match in DOCUMENT order, not the first selector's match, so an incidental
 * `.col-12.pt-2` earlier in the row — a centered destination name, a stealth prompt — would outrank
 * the portrait the renderer deliberately marked, and the prose walk would then delete that block's
 * text instead of the portrait's. The marker wins; the legacy shape is consulted only when no
 * marked block exists at all.
 */
export function targetBlockIn(row: Element): Element | null {
    return row.querySelector(TARGET_MARKER) || row.querySelector(TARGET_BLOCK_LEGACY);
}

/**
 * Who it happened TO. The owner's "sometimes we don't have the target of the effect": a status
 * effect, an attack or a heal names its target in a trailing portrait block, which the prose walk
 * reduced to a bare tooltip string or dropped entirely.
 */
export function targetFrom(row: Element): FtLogActor | undefined {
    const block = targetBlockIn(row);
    if (!block) return undefined;
    // The block is a wrapper; the portrait itself is the thumbnail inside it.
    return actorFromCell(block.querySelector('[data-ft-item-id]')?.closest('div') || block);
}

/** Trade a spelled-out event phrase for a glyph, and report which words the glyph now covers. */
export function eventIconFor(text: string): { icon: string; matched: RegExp } | null {
    for (const [pattern, icon] of PHRASE_ICONS) {
        if (pattern.test(text)) return { icon, matched: pattern };
    }
    return null;
}
